import type { AnclaCuota, CuotaCotizacion, CuotaLeida } from "./api";
import { formatFecha } from "./format";

/**
 * Forma de pago de un presupuesto recibido (docs/modulo-cotizaciones.md § Forma de pago): cada pago es un % del total
 * y cae en una fecha (opcional) o según el cronograma (docs/modulo-cash-flow.md § Cuándo cae cada pago). Los % se
 * cuentan en centésimas enteras (70 % = 7000): nunca con float.
 */

export const MAX_CONCEPTO = 100;
export const MAX_CUOTAS_QUINCENALES = 48;
export const MAX_DIAS_ANCLA = 999;
const CIEN = 10000; // 100 % en centésimas

/** Lo que se elige en "Cuándo" (sin fecha = "En una fecha" con la fecha vacía). */
export type AnclaForm = Exclude<AnclaCuota, "sin_fecha">;

export const OPCIONES_ANCLA: { valor: AnclaForm; etiqueta: string }[] = [
  { valor: "fecha", etiqueta: "En una fecha" },
  { valor: "inicio_rubro", etiqueta: "Al empezar un rubro" },
  { valor: "fin_rubro", etiqueta: "Al terminar un rubro" },
  { valor: "durante_rubro", etiqueta: "Mientras dura un rubro (repartido)" },
  { valor: "inicio_item", etiqueta: "Al empezar un ítem" },
  { valor: "fin_item", etiqueta: "Al terminar un ítem" },
];

const ANCLAS_RUBRO: AnclaCuota[] = ["inicio_rubro", "fin_rubro", "durante_rubro"];
const ANCLAS_ITEM: AnclaCuota[] = ["inicio_item", "fin_item"];
export const esAnclaRubro = (a: AnclaCuota) => ANCLAS_RUBRO.includes(a);
export const esAnclaItem = (a: AnclaCuota) => ANCLAS_ITEM.includes(a);
/** Al empezar o terminar (no "mientras dura"): llevan días ±. */
export const llevaDias = (a: AnclaCuota) => esAnclaItem(a) || a === "inicio_rubro" || a === "fin_rubro";

export interface FormCuota {
  concepto: string;
  porcentaje: string;
  ancla: AnclaForm;
  fecha: string;
  rubroId: string;
  itemId: string;
  /** Corridos, con signo ("-5", "30"); vacío = 0. */
  dias: string;
}

export interface PayloadCuota {
  concepto: string;
  /** "70.00" */
  porcentaje: string;
  fecha: string | null;
  /** Sin ancla, el backend la saca de la fecha (fija o sin fecha). */
  ancla?: AnclaCuota;
  rubro_id?: number | null;
  item_id?: number | null;
  dias?: number;
}

export type ErroresCuota = Partial<Record<keyof FormCuota, string>>;

/** "70" → 7000, "33,33" → 3333; vacío, texto o más de 2 decimales → null. */
export function aCentesimas(texto: string): number | null {
  const m = /^(\d{1,6})(?:[.,](\d{1,2}))?$/.exec(texto.trim());
  if (!m) return null;
  return Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
}

/** 3333 → "33.33" (para la API). */
export function deCentesimas(n: number): string {
  return `${Math.trunc(n / 100)}.${String(n % 100).padStart(2, "0")}`;
}

/** "33.30" → "33,3": sin ceros de más y con coma, para un input. */
export function porcentajeAInput(api: string): string {
  return (api.includes(".") ? api.replace(/\.?0+$/, "") : api).replace(".", ",");
}

/** "70.00" → "70 %", "33.33" → "33,33 %". */
export function formatPorcentajeCuota(api: string): string {
  return `${porcentajeAInput(api)} %`;
}

export function formCuota(c?: CuotaCotizacion): FormCuota {
  if (!c) return { concepto: "", porcentaje: "", ancla: "fecha", fecha: "", rubroId: "", itemId: "", dias: "" };
  return {
    concepto: c.concepto,
    porcentaje: porcentajeAInput(c.porcentaje),
    ancla: c.ancla === "sin_fecha" ? "fecha" : c.ancla,
    fecha: c.fecha ?? "",
    rubroId: c.rubro_id === null ? "" : String(c.rubro_id),
    itemId: c.item_id === null ? "" : String(c.item_id),
    dias: c.dias === 0 ? "" : String(c.dias),
  };
}

function errorPorcentaje(texto: string): string | undefined {
  if (!texto.trim()) return "Cargá el % del pago";
  const n = aCentesimas(texto);
  if (n === null) return /^\d+[.,]\d{3,}$/.test(texto.trim()) ? "El % del pago tiene hasta 2 decimales" : "Cargá un % válido";
  if (n <= 0) return "El % del pago tiene que ser mayor a cero";
  if (n > CIEN) return "El % del pago no puede pasar de 100";
  return undefined;
}

/** Las mismas reglas que `reglas.validar_cuota` del backend. */
export function validarCuota(f: FormCuota): ErroresCuota {
  const errores: ErroresCuota = {};
  const concepto = f.concepto.trim().replace(/\s+/g, " ");
  if (!concepto) errores.concepto = "Escribí el concepto del pago";
  else if (concepto.length > MAX_CONCEPTO) errores.concepto = `El concepto tiene hasta ${MAX_CONCEPTO} caracteres`;
  const porcentaje = errorPorcentaje(f.porcentaje);
  if (porcentaje) errores.porcentaje = porcentaje;
  if (esAnclaRubro(f.ancla) && !f.rubroId) errores.rubroId = "Elegí el rubro del pago";
  if (esAnclaItem(f.ancla) && !f.itemId) errores.itemId = "Elegí el ítem del pago";
  if (llevaDias(f.ancla) && f.dias.trim()) {
    const n = Number(f.dias.trim());
    if (!/^-?\d+$/.test(f.dias.trim()) || Math.abs(n) > MAX_DIAS_ANCLA) errores.dias = `Los días van de -${MAX_DIAS_ANCLA} a ${MAX_DIAS_ANCLA}`;
  }
  return errores;
}

export function aPayloadCuota(f: FormCuota): PayloadCuota {
  const ancla: AnclaCuota = f.ancla === "fecha" && !f.fecha ? "sin_fecha" : f.ancla;
  return {
    concepto: f.concepto.trim().replace(/\s+/g, " "),
    porcentaje: deCentesimas(aCentesimas(f.porcentaje) ?? 0),
    fecha: ancla === "fecha" ? f.fecha : null,
    ancla,
    rubro_id: esAnclaRubro(ancla) ? Number(f.rubroId) : null,
    item_id: esAnclaItem(ancla) ? Number(f.itemId) : null,
    dias: llevaDias(ancla) && f.dias.trim() ? Number(f.dias.trim()) : 0,
  };
}

const textoDias = (n: number) => (n === 0 ? "" : ` ${n > 0 ? "+" : "−"} ${Math.abs(n)} ${Math.abs(n) === 1 ? "día" : "días"}`);

/** "01/11/2026", "Sin fecha", "Al terminar Albañilería + 30 días", "Mientras dura Albañilería". */
export function textoCuando(c: { ancla: AnclaCuota; fecha: string | null; dias?: number; rubro?: string | null; item?: string | null }): string {
  const dias = textoDias(c.dias ?? 0);
  const rubro = c.rubro ?? "el rubro";
  const item = c.item ?? "el ítem";
  switch (c.ancla) {
    case "fecha":
      return c.fecha ? formatFecha(c.fecha) : "Sin fecha";
    case "sin_fecha":
      return "Sin fecha";
    case "inicio_rubro":
      return `Al empezar ${rubro}${dias}`;
    case "fin_rubro":
      return `Al terminar ${rubro}${dias}`;
    case "durante_rubro":
      return `Mientras dura ${rubro}`;
    case "inicio_item":
      return `Al empezar ${item}${dias}`;
    case "fin_item":
      return `Al terminar ${item}${dias}`;
  }
}

/** Para los anclados al cronograma: cuándo cae ("Cae el 02/11/2026", "Del 02/11/2026 al 20/11/2026") o por qué no. */
export function textoPrevista(c: CuotaCotizacion): string | null {
  if (c.ancla === "fecha" || c.ancla === "sin_fecha") return null;
  if (c.prevista && c.prevista_hasta) return `Del ${formatFecha(c.prevista)} al ${formatFecha(c.prevista_hasta)}`;
  if (c.prevista) return `Cae el ${formatFecha(c.prevista)}`;
  return c.motivo;
}

/** La forma de pago leída por el escaneo, como va en el alta. */
export function cuotasDesdeEscaneo(cuotas: CuotaLeida[]): PayloadCuota[] {
  return cuotas.map((c) => ({ concepto: c.concepto, porcentaje: c.porcentaje, fecha: c.fecha }));
}

/** "2026-12-25" + 15 → "2027-01-09" (en UTC, sin husos). */
export function sumarDias(iso: string, dias: number): string {
  const [a = 0, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
}

/** "Faltan 10 %" cuando hay pagos y no suman el total; null si suman 100 o no hay plan. */
export function avisoPorcentajes(cantidad: number, suma: string): string | null {
  const n = aCentesimas(suma);
  if (cantidad === 0 || n === null || n === CIEN) return null;
  const resto = deCentesimas(Math.abs(CIEN - n));
  return `Los pagos suman ${formatPorcentajeCuota(suma)} del total: ${n < CIEN ? "falta" : "sobra"} ${formatPorcentajeCuota(resto)}.`;
}

// ------------------------------------------------------------------ atajos (arman el plan entero)

export type TipoAtajo = "acopio" | "anticipo" | "quincenales";

export const TITULO_ATAJO: Record<TipoAtajo, string> = { acopio: "Acopio", anticipo: "Anticipo y saldo", quincenales: "Cuotas quincenales" };

export interface FormAtajo {
  tipo: TipoAtajo;
  /** En fechas, o según el rubro (al empezar, al terminar y cada 15 días desde que empieza). */
  segun: "fechas" | "rubro";
  rubroId: string;
  /** El anticipo en % (en Acopio no se usa: es el 100 %). */
  porcentaje: string;
  /** Cantidad de cuotas quincenales. */
  cantidad: string;
  /** Del acopio o del anticipo. */
  fecha: string;
  /** Del saldo o de la primera cuota. */
  fechaSiguiente: string;
}

/** Aviso del atajo cuando ya hay un plan cargado. */
export function textoReemplazo(cantidad: number): string | null {
  if (cantidad === 0) return null;
  return cantidad === 1 ? "Reemplaza el pago cargado." : `Reemplaza los ${cantidad} pagos cargados.`;
}

export type ErroresAtajo = Partial<Record<"porcentaje" | "cantidad" | "rubroId", string>>;

export function formAtajo(tipo: TipoAtajo): FormAtajo {
  return { tipo, segun: "fechas", rubroId: "", porcentaje: tipo === "anticipo" ? "70" : tipo === "quincenales" ? "0" : "", cantidad: tipo === "quincenales" ? "6" : "", fecha: "", fechaSiguiente: "" };
}

function anticipo(f: FormAtajo): number | null {
  return f.tipo === "quincenales" && !f.porcentaje.trim() ? 0 : aCentesimas(f.porcentaje);
}

export function validarAtajo(f: FormAtajo): ErroresAtajo {
  const errores: ErroresAtajo = {};
  if (f.segun === "rubro" && !f.rubroId) errores.rubroId = "Elegí el rubro";
  if (f.tipo === "acopio") return errores;
  const a = anticipo(f);
  if (a === null) errores.porcentaje = "Cargá un % válido, con hasta 2 decimales";
  else if (a >= CIEN) errores.porcentaje = "El anticipo tiene que ser menor a 100";
  else if (f.tipo === "anticipo" && a === 0) errores.porcentaje = "El anticipo tiene que ser mayor a cero";
  if (f.tipo === "quincenales") {
    const n = Number(f.cantidad.trim());
    if (!f.cantidad.trim()) errores.cantidad = "Cargá la cantidad de cuotas";
    else if (!/^\d+$/.test(f.cantidad.trim()) || n < 1 || n > MAX_CUOTAS_QUINCENALES) errores.cantidad = `Entre 1 y ${MAX_CUOTAS_QUINCENALES} cuotas`;
    else if (a !== null && a < CIEN && CIEN - a < n) errores.cantidad = `Con ese anticipo no alcanza para ${n} cuotas`;
  }
  return errores;
}

/** El plan que arma el atajo (validado antes con `validarAtajo`). Según el rubro: el acopio y el anticipo al empezar,
 * el saldo al terminar y las cuotas cada 15 días desde que empieza (la primera, a los 15 si hay anticipo). */
export function armarPlan(f: FormAtajo): PayloadCuota[] {
  if (f.segun === "rubro") return armarPlanRubro(f, Number(f.rubroId));
  const fecha = (iso: string) => iso || null;
  if (f.tipo === "acopio") return [{ concepto: "Acopio", porcentaje: deCentesimas(CIEN), fecha: fecha(f.fecha) }];
  const a = anticipo(f) ?? 0;
  if (f.tipo === "anticipo") {
    return [
      { concepto: "Anticipo", porcentaje: deCentesimas(a), fecha: fecha(f.fecha) },
      { concepto: "Saldo", porcentaje: deCentesimas(CIEN - a), fecha: fecha(f.fechaSiguiente) },
    ];
  }
  const n = Number(f.cantidad);
  const resto = CIEN - a;
  const cuota = Math.floor(resto / n);
  const cuotas = Array.from({ length: n }, (_, i) => ({
    concepto: `Cuota ${i + 1} de ${n}`,
    porcentaje: deCentesimas(i === n - 1 ? resto - cuota * (n - 1) : cuota),
    fecha: f.fechaSiguiente ? sumarDias(f.fechaSiguiente, 15 * i) : null,
  }));
  return a > 0 ? [{ concepto: "Anticipo", porcentaje: deCentesimas(a), fecha: fecha(f.fecha) }, ...cuotas] : cuotas;
}

function armarPlanRubro(f: FormAtajo, rubroId: number): PayloadCuota[] {
  const en = (ancla: AnclaCuota, dias = 0) => ({ fecha: null, ancla, rubro_id: rubroId, item_id: null, dias });
  if (f.tipo === "acopio") return [{ concepto: "Acopio", porcentaje: deCentesimas(CIEN), ...en("inicio_rubro") }];
  const a = anticipo(f) ?? 0;
  if (f.tipo === "anticipo") {
    return [
      { concepto: "Anticipo", porcentaje: deCentesimas(a), ...en("inicio_rubro") },
      { concepto: "Saldo", porcentaje: deCentesimas(CIEN - a), ...en("fin_rubro") },
    ];
  }
  const n = Number(f.cantidad);
  const resto = CIEN - a;
  const cuota = Math.floor(resto / n);
  const desde = a > 0 ? 1 : 0;
  const cuotas = Array.from({ length: n }, (_, i) => ({
    concepto: `Cuota ${i + 1} de ${n}`,
    porcentaje: deCentesimas(i === n - 1 ? resto - cuota * (n - 1) : cuota),
    ...en("inicio_rubro", 15 * (i + desde)),
  }));
  return a > 0 ? [{ concepto: "Anticipo", porcentaje: deCentesimas(a), ...en("inicio_rubro") }, ...cuotas] : cuotas;
}
